import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import { ErrorPage, NotFoundPage } from "@/components/error-page";
import { PendingPage } from "@/components/pending-page";
import { routeTree } from "./routeTree.gen";
import "./index.css";
// Stamps the theme on <html> for every route, shell or not.
import "@/stores/theme";
import { queryClient } from "@/lib/query-client";

// Alt+click an element to open its source; the runtime and its data
// attributes exist only in the dev build.
if (import.meta.env.DEV) {
  void import("@locator/runtime").then((locator) => locator.default());
}

const router = createRouter({
  routeTree,
  context: { queryClient },
  // A thrown render or loader error is a page of its own, not the router's
  // bare default — and the same page wherever it is thrown.
  defaultErrorComponent: ErrorPage,
  defaultNotFoundComponent: NotFoundPage,
  // A route whose loader is still running shows this rather than the blank the
  // router falls back to. `_app` overrides it with a skeleton of its own shell;
  // this is for every other route with a loader, which cannot be given a
  // page-shaped skeleton from here because nothing here knows the page shape.
  defaultPendingComponent: PendingPage,
  defaultPreload: "intent",
  // The loaders read through the query client, which already dedupes.
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Missing #root element");

createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
